import { useState } from 'react';
import { Mail, MessageSquare, Phone, Send, MapPin } from 'lucide-react';
import MarketingLayout from './_layout';

const channels = [
  { icon: Mail, label: 'Email', value: 'support@messego.com', href: 'mailto:support@messego.com' },
  { icon: MessageSquare, label: 'Live Chat', value: 'Available 24/7 in app', href: '#' },
  { icon: Phone, label: 'Phone', value: '+1 (415) 555-0123', href: 'tel:+14155550123' },
];

const offices = [
  { city: 'London', address: '14 Old Street, London EC1V 9BG, UK' },
  { city: 'New York', address: '450 Park Avenue, New York, NY 10022, USA' },
  { city: 'Lisbon', address: 'Av. da Liberdade 110, 1250-146 Lisboa, Portugal' },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <MarketingLayout
      title="Contact us"
      subtitle="Pick the channel that suits you best. Our team typically responds within a few hours."
    >
      <section className="space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {channels.map((c, i) => (
            <a
              key={i}
              href={c.href}
              className="p-6 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl space-y-3 hover:shadow-lg transition-all"
            >
              <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex items-center justify-center">
                <c.icon size={18} />
              </div>
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-bold">{c.label}</p>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">{c.value}</p>
              </div>
            </a>
          ))}
        </div>

        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-[2rem] p-8 space-y-6">
          <div className="space-y-1">
            <h2 className="text-xl font-bold">Send us a message</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">We typically respond within a few hours.</p>
          </div>

          {submitted ? (
            <div className="p-6 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 rounded-2xl text-sm">
              Thanks — your message is on its way. We'll be in touch shortly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Name</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Subject</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Message</label>
                <textarea
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all resize-none"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-900 text-white rounded-xl font-bold hover:bg-neutral-800 transition-all shadow-lg shadow-neutral-900/20"
              >
                Send Message <Send size={16} />
              </button>
            </form>
          )}
        </div>

        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Offices</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {offices.map((o, i) => (
              <div key={i} className="p-6 bg-neutral-50 dark:bg-neutral-950 rounded-2xl border border-neutral-200 dark:border-neutral-800 space-y-2">
                <div className="flex items-center gap-2 font-bold">
                  <MapPin size={14} /> {o.city}
                </div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{o.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
